import React, { useState } from "react";
import {
    Typography,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Input,
    CircularProgress,
    Grow,
    Grid,
    Card,
    CardMedia,
    CardContent,
} from "@mui/material";
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from "firebase/storage"; 

export const SubDepartmentDialog = (props) => {
    const storageInstance = getStorage();
    const [imageUploading, setImageUploading] = useState(false);
    const [iconUploading, setIconUploading] = useState(false);
    const [pdfUploading, setPdfUploading] = useState(false);
    const { openSubDepartmentDialog, handleCloseSubDepartmentDialog, subDepartmentDialogData, setSubDepartmentDialogData, handleSaveSubDepartment } = props;

    // Separate handler for main image upload
    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const storageRef = ref(storageInstance, `departmentImages/${file.name}`);
            const uploadTask = uploadBytesResumable(storageRef, file);

            uploadTask.on(
                'state_changed',
                () => {
                    setImageUploading(true);
                },
                (error) => {
                    console.error('Error uploading image:', error);
                    setImageUploading(false);
                },
                () => {
                    getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
                        setSubDepartmentDialogData((prevData) => ({
                            ...prevData,
                            image: downloadURL,  // Update image field
                        }));
                        setImageUploading(false);
                    });
                }
            );
        }
    };

    // Separate handler for icon upload
    const handleIconUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const storageRef = ref(storageInstance, `iconImages/${file.name}`);
            const uploadTask = uploadBytesResumable(storageRef, file);

            uploadTask.on(
                'state_changed',
                () => {
                    setIconUploading(true);
                },
                (error) => {
                    console.error('Error uploading icon:', error);
                    setIconUploading(false);
                },
                () => {
                    getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
                        setSubDepartmentDialogData((prevData) => ({
                            ...prevData,
                            iconImage: downloadURL,  // Update iconImage field
                        }));
                        setIconUploading(false);
                    });
                }
            );
        }
    };

    const handlePdfUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const storageRef = ref(storageInstance, `pdfs/${file.name}`);
            const uploadTask = uploadBytesResumable(storageRef, file);

            uploadTask.on(
                'state_changed',
                () => {
                    setPdfUploading(true);
                },
                (error) => {
                    console.error('Error uploading PDF:', error);
                    setPdfUploading(false);
                },
                () => {
                    getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
                        setSubDepartmentDialogData((prevData) => ({
                            ...prevData,
                            pdfUrl: downloadURL,
                        }));
                        setPdfUploading(false);
                    });
                }
            );
        }
    };

    return (
        <Dialog
            open={openSubDepartmentDialog}
            TransitionComponent={Grow}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    padding: 3,
                    borderRadius: "15px",
                    boxShadow: "0 8px 20px rgba(0, 0, 0, 0.1)",
                },
            }}
        >
            <DialogTitle
                sx={{
                    backgroundColor: '#1976d2',
                    color: '#fff',
                    fontWeight: 'bold',
                    fontSize: '1.25rem',
                }}
            >
                {subDepartmentDialogData.id ? "Edit Sub-Department" : "Add Sub-Department"}
            </DialogTitle>

            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            label="Name"
                            variant="outlined"
                            fullWidth
                            value={subDepartmentDialogData.name}
                            onChange={(e) =>
                                setSubDepartmentDialogData((prevData) => ({
                                    ...prevData,
                                    name: e.target.value,
                                }))
                            }
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Description"
                            variant="outlined"
                            fullWidth
                            value={subDepartmentDialogData.description}
                            onChange={(e) =>
                                setSubDepartmentDialogData((prevData) => ({
                                    ...prevData,
                                    description: e.target.value,
                                }))
                            }
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Definition"
                            variant="outlined"
                            fullWidth
                            value={subDepartmentDialogData.definition}
                            onChange={(e) =>
                                setSubDepartmentDialogData((prevData) => ({
                                    ...prevData,
                                    definition: e.target.value,
                                }))
                            }
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Author"
                            variant="outlined"
                            fullWidth
                            value={subDepartmentDialogData.author}
                            onChange={(e) =>
                                setSubDepartmentDialogData((prevData) => ({
                                    ...prevData,
                                    author: e.target.value,
                                }))
                            }
                        />
                    </Grid>

                    {/* Image & Icon section (md=6 each) */}
                    <Grid item xs={12} md={6}>
                        <Input
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            sx={{ mb: 2 }}
                        />
                        {subDepartmentDialogData.image && (
                            <Card
                                sx={{
                                    maxWidth: 200,
                                    mx: "auto",
                                    mb: 2,
                                    transition: "transform 0.5s",
                                    "&:hover": { transform: "rotateY(180deg)" },
                                }}
                            >
                                <CardMedia
                                    component="img"
                                    height="140"
                                    image={subDepartmentDialogData.image}
                                    alt={subDepartmentDialogData.name}
                                />
                                <CardContent>
                                    <Typography variant="body2" color="text.secondary">
                                        Image Preview
                                    </Typography>
                                </CardContent>
                            </Card>
                        )}
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Input
                            type="file"
                            accept="image/*"
                            onChange={handleIconUpload}  // Separate handler for icon
                            sx={{ mb: 2 }}
                        />
                        {subDepartmentDialogData.iconImage && (
                            <Card
                                sx={{
                                    maxWidth: 200,
                                    mx: "auto",
                                    mb: 2,
                                    transition: "transform 0.5s",
                                    "&:hover": { transform: "rotateY(180deg)" },
                                }}
                            >
                                <CardMedia
                                    component="img"
                                    height="140"
                                    image={subDepartmentDialogData.iconImage}
                                    alt={subDepartmentDialogData.name}
                                />
                                <CardContent>
                                    <Typography variant="body2" color="text.secondary">
                                        Icon Preview
                                    </Typography>
                                </CardContent>
                            </Card>
                        )}
                    </Grid>

                    {/* PDF Upload (md=12 full width) */}
                    <Grid item xs={12}>
                        <Button
                            variant="contained"
                            component="label"
                            sx={{
                                borderRadius: '8px',
                                backgroundColor: '#1976d2',
                                '&:hover': {
                                    backgroundColor: '#115293'
                                }
                            }}
                        >
                            Upload PDF
                            <input hidden accept="application/pdf" type="file" onChange={handlePdfUpload} />
                        </Button>
                        {subDepartmentDialogData.pdfUrl && (
                            <Card sx={{ maxWidth: 300, mx: "auto", mt: 2 }}>
                                <CardContent>
                                    <Typography variant="body2" color="text.secondary">
                                        <a href={subDepartmentDialogData.pdfUrl} target="_blank" rel="noopener noreferrer">
                                            View PDF
                                        </a>
                                    </Typography>
                                </CardContent>
                            </Card>
                        )}
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions sx={{ justifyContent: "center", mt: 2 }}>
                <Button onClick={handleCloseSubDepartmentDialog} color="secondary">
                    Cancel
                </Button>
                <Button onClick={handleSaveSubDepartment} variant="contained" color="primary">
                    {imageUploading || iconUploading || pdfUploading ? <CircularProgress size={24} /> : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
